/**
 * TaskTemplatePicker — Drawer with two tabs: Built-in scenarios + Saved user templates.
 *
 * Loads templates from backend via useTaskTemplates hook (DynamoDB + Valkey).
 * Falls back to hardcoded BUILTIN_SCENARIOS when backend is unavailable.
 *
 * On selection the chosen template's state is applied back to the form via applyFormState.
 */

import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconFileImport,
  IconFlame,
  IconStar,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';
import React from 'react';
import {
  type TaskFormState,
  type TaskFormSetters,
  applyFormState,
} from './taskFormState';
import { useTaskTemplates, type TaskTemplateView } from '../hooks/useTaskTemplates';

export interface TaskTemplatePickerProps {
  opened: boolean;
  onClose: () => void;
  /** Setters map for distributing state back to form */
  setters: Partial<TaskFormSetters>;
  /** Called after a template is loaded so the parent can do any additional work */
  onLoaded?: (state: Partial<TaskFormState>) => void;
}

export const TaskTemplatePicker: React.FC<TaskTemplatePickerProps> = ({
  opened,
  onClose,
  setters,
  onLoaded,
}) => {
  const {
    builtinTemplates,
    userTemplates,
    loading,
    deleteTemplate,
  } = useTaskTemplates();

  const handleSelect = (template: TaskTemplateView) => {
    if (!template.formState) return;

    // Remove undefined entries
    const clean = Object.fromEntries(
      Object.entries(template.formState).filter(([, v]) => v !== undefined),
    ) as Partial<TaskFormState>;

    applyFormState(clean, setters);
    onLoaded?.(clean);
    onClose();
  };

  const handleDelete = async (id: string) => {
    await deleteTemplate(id);
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'Published': return 'green';
      case 'Draft': return 'yellow';
      case 'PendingApproval': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconFileImport size={20} />
          <Text fw={600}>Load Template</Text>
        </Group>
      }
      position="right"
      size="md"
    >
      <Tabs defaultValue="builtin">
        <Tabs.List grow mb="md">
          <Tabs.Tab value="builtin" leftSection={<IconFlame size={14} />}>
            Built-in ({builtinTemplates.length})
          </Tabs.Tab>
          <Tabs.Tab value="saved" leftSection={<IconUser size={14} />}>
            Saved ({userTemplates.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="builtin">
          {loading ? (
            <Stack align="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Loading templates...</Text>
            </Stack>
          ) : (
            <Stack gap="sm">
              {builtinTemplates.map((t) => (
                <Card key={t.id} withBorder padding="sm" style={{ cursor: 'pointer' }}>
                  <Group justify="space-between" mb={4}>
                    <Group gap="xs">
                      <Badge size="sm" color={t.color}>{t.category}</Badge>
                      <Text fw={600} size="sm">{t.name}</Text>
                    </Group>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconStar size={14} />}
                      onClick={() => handleSelect(t)}
                      disabled={!t.formState}
                    >
                      Load
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed">{t.description}</Text>
                  <Group gap={4} mt={4}>
                    {t.features.slice(0, 3).map((f) => (
                      <Badge key={f} size="xs" variant="outline">{f}</Badge>
                    ))}
                    {t.features.length > 3 && (
                      <Badge size="xs" variant="outline" color="gray">
                        +{t.features.length - 3}
                      </Badge>
                    )}
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="saved">
          {loading ? (
            <Stack align="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Loading templates...</Text>
            </Stack>
          ) : (
            <Stack gap="sm">
              {userTemplates.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No saved templates yet. Fill out a form and click &quot;Save as Template&quot;.
                </Text>
              )}
              {userTemplates.map((t) => (
                <Card key={t.id} withBorder padding="sm">
                  <Group justify="space-between" mb={4}>
                    <Group gap="xs">
                      <Badge size="sm" color={statusColor(t.status)}>{t.status}</Badge>
                      <Badge size="sm" color="teal">{t.category}</Badge>
                      <Text fw={600} size="sm">{t.name}</Text>
                    </Group>
                    <Group gap={4}>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => handleSelect(t)}
                        disabled={!t.formState}
                      >
                        Load
                      </Button>
                      <Tooltip label="Delete template" withArrow>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => handleDelete(t.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                  {t.description && <Text size="xs" c="dimmed">{t.description}</Text>}
                  <Group gap={4} mt={4}>
                    {t.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} size="xs" variant="outline">{tag}</Badge>
                    ))}
                  </Group>
                  {t.createdAt && (
                    <Text size="xs" c="dimmed" mt={4}>
                      Created {new Date(t.createdAt).toLocaleDateString()}
                    </Text>
                  )}
                </Card>
              ))}
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
    </Drawer>
  );
};

export default TaskTemplatePicker;
